import type { StaticLessonContent } from "@/lib/training-api";
import {
  LESSON_PARENTS,
  type LessonParent,
} from "./catalogue";
import {
  COUNTRY_DIFF_PARENTS,
  type LessonCountryDiffParent,
} from "./country-diffs/catalogue";

import enLessons from "./translations/en/lessons.json";
import esLessons from "./translations/es/lessons.json";
import hiLessons from "./translations/hi/lessons.json";
import zhCNLessons from "./translations/zh-CN/lessons.json";
import tlLessons from "./translations/tl/lessons.json";
import trLessons from "./translations/tr/lessons.json";

import enCountryDiffs from "./country-diffs/translations/en/country-diffs.json";
import esCountryDiffs from "./country-diffs/translations/es/country-diffs.json";
import hiCountryDiffs from "./country-diffs/translations/hi/country-diffs.json";
import zhCNCountryDiffs from "./country-diffs/translations/zh-CN/country-diffs.json";
import tlCountryDiffs from "./country-diffs/translations/tl/country-diffs.json";
import trCountryDiffs from "./country-diffs/translations/tr/country-diffs.json";

/**
 * Public-API adapter (ADR-0015 Phase A+B). Assembles parent + per-locale
 * translations into the legacy `StaticLessonContent` shape that
 * `training-api.ts` consumes.
 *
 * Phase B (ROADS-529 follow-up, 2026-05-24): adapter is now LOCALE-AWARE.
 * Callers pass an i18next BCP-47 tag (en, es, hi, zh-CN, tl, tr).
 * Falls back to en when a tag is unknown or a key is missing.
 *
 * Field-of-record:
 * - Parent (language-invariant): `./catalogue.ts`
 * - Translations: `./translations/{lang}/lessons.json`
 * - Country-diff parent: `./country-diffs/catalogue.ts`
 * - Country-diff translations: `./country-diffs/translations/{lang}/country-diffs.json`
 */

interface LessonTranslation {
  title: string;
  summary: string;
  content: StaticLessonContent["content"];
}
interface CountryDiffTranslation {
  country_name: string;
  summary: string;
  details: string;
}

type LessonBundle = Record<string, LessonTranslation>;
type CountryDiffBundle = Record<string, CountryDiffTranslation>;

const LESSON_BUNDLES: Record<string, LessonBundle> = {
  en: enLessons as LessonBundle,
  es: esLessons as LessonBundle,
  hi: hiLessons as LessonBundle,
  "zh-CN": zhCNLessons as LessonBundle,
  tl: tlLessons as LessonBundle,
  tr: trLessons as LessonBundle,
};

const COUNTRY_DIFF_BUNDLES: Record<string, CountryDiffBundle> = {
  en: enCountryDiffs as CountryDiffBundle,
  es: esCountryDiffs as CountryDiffBundle,
  hi: hiCountryDiffs as CountryDiffBundle,
  "zh-CN": zhCNCountryDiffs as CountryDiffBundle,
  tl: tlCountryDiffs as CountryDiffBundle,
  tr: trCountryDiffs as CountryDiffBundle,
};

function resolveLocale(lang: string): string {
  if (lang in LESSON_BUNDLES) return lang;
  // Try primary subtag: "zh-Hans" → "zh-CN" if mapped, else "en".
  const primary = lang.split("-")[0];
  if (primary in LESSON_BUNDLES) return primary;
  if (primary === "zh") return "zh-CN";
  return "en";
}

function assembleLesson(
  p: LessonParent,
  lessonTrans: LessonBundle,
  cdTrans: CountryDiffBundle,
  fallbackLessonTrans: LessonBundle,
  fallbackCdTrans: CountryDiffBundle,
): StaticLessonContent {
  const t = lessonTrans[p.id] ?? fallbackLessonTrans[p.id];
  if (!t) throw new Error(`Missing translation for lesson ${p.id}`);
  const countryDiffs = p.country_diff_ids.map((id) => {
    const cdParent = COUNTRY_DIFF_PARENTS.find((cd) => cd.id === id);
    const cdT = cdTrans[id] ?? fallbackCdTrans[id];
    if (!cdParent) throw new Error(`Missing country-diff parent ${id}`);
    if (!cdT) throw new Error(`Missing translation for country-diff ${id}`);
    return {
      country_code: cdParent.country_code,
      country_name: cdT.country_name,
      flag: cdParent.flag,
      severity: cdParent.severity,
      summary: cdT.summary,
      details: cdT.details,
    };
  });
  return {
    id: p.id,
    title: t.title,
    category: p.category,
    duration_min: p.duration_min,
    emoji: p.emoji,
    colorFrom: p.colorFrom,
    colorTo: p.colorTo,
    summary: t.summary,
    tags: [...p.tags],
    content: t.content,
    country_diffs: countryDiffs,
    related_lesson_ids: [...p.related_lesson_ids],
  };
}

/**
 * Get the full lesson set for a given locale. ROADS-529 Phase B.
 * Caller (training-api.ts) passes i18next.language; we resolve to a
 * supported bundle (en/es/hi/zh-CN/tl/tr), fall back to en for the
 * unknown-tag or missing-key cases.
 */
export function getLessons(lang: string): StaticLessonContent[] {
  const loc = resolveLocale(lang);
  const lessonTrans = LESSON_BUNDLES[loc];
  const cdTrans = COUNTRY_DIFF_BUNDLES[loc];
  return LESSON_PARENTS.map((p) =>
    assembleLesson(p, lessonTrans, cdTrans, LESSON_BUNDLES.en, COUNTRY_DIFF_BUNDLES.en),
  );
}

/**
 * Backwards-compat: callers still importing `LESSONS` get the English
 * bundle. Deprecated — switch to `getLessons(i18n.language)` at call
 * sites that have access to the user's locale (every React-mounted call).
 */
export const LESSONS: StaticLessonContent[] = getLessons("en");

// Re-export parent shapes so future phases can read them without
// going through the assembled `LESSONS` const.
export { LESSON_PARENTS, COUNTRY_DIFF_PARENTS };
export type { LessonParent, LessonCountryDiffParent };
